import { ErrorHandler, Injectable, Injector } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import * as Sentry from '@sentry/angular-ivy'
import { NotificationService } from './notification.service'
import { TranslateService } from '@ngx-translate/core'

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private sentryErrorHandler = Sentry.createErrorHandler({
    showDialog: false,
  })

  constructor(private injector: Injector) {}

  handleError(error: any) {
    const rawError = error?.ngOriginalError || error?.rejection || error?.reason || error
    const underlyingError =
      rawError && typeof rawError === 'object' && (rawError as any).error instanceof Error
        ? (rawError as any).error
        : rawError

    let status = 0

    // Extract status from different error types
    if (error instanceof HttpErrorResponse) {
      status = error.status
    } else if (rawError && rawError.status) {
      status = rawError.status
    } else if (underlyingError && underlyingError.status) {
      status = underlyingError.status
    } else if (error && error.status) {
      status = error.status
    }

    if (status === 502) {
      try {
        const notificationService = this.injector.get(NotificationService)
        const translateService = this.injector.get(TranslateService)
        notificationService.error(
          translateService.instant('error.502.message'),
          translateService.instant('error.502.title'),
        )
      } catch (e) {
        console.error('Could not show 502 notification', e)
      }
      return
    }

    // Ignore cancelled fetch requests, network disconnects, and browser navigation aborts
    const errorStrings: string[] = [
      error?.name,
      error?.message,
      error?.rejection?.name,
      error?.rejection?.message,
      error?.reason?.name,
      error?.reason?.message,
      rawError?.name,
      rawError?.message,
      underlyingError?.name,
      underlyingError?.message,
      typeof error === 'string' ? error : '',
      typeof rawError === 'string' ? rawError : '',
      typeof underlyingError === 'string' ? underlyingError : '',
      typeof error?.toString === 'function' ? error.toString() : '',
      typeof rawError?.toString === 'function' ? rawError.toString() : '',
      typeof underlyingError?.toString === 'function' ? underlyingError.toString() : '',
    ]
    const combinedErrorText = errorStrings.filter(Boolean).join(' ')

    const isNetworkOrAbortError =
      status === 499 ||
      errorStrings.some(s => s === 'AbortError') ||
      /AbortError/i.test(combinedErrorText) ||
      /The user aborted a request/i.test(combinedErrorText) ||
      /The operation was aborted/i.test(combinedErrorText) ||
      /Failed to fetch/i.test(combinedErrorText) ||
      /NetworkError/i.test(combinedErrorText) ||
      /Load failed/i.test(combinedErrorText) ||
      /Fetch API cannot load/i.test(combinedErrorText) ||
      /Network request failed/i.test(combinedErrorText)

    if (isNetworkOrAbortError) {
      return
    }

    if (combinedErrorText.includes('/assets/i18n/')) {
      try {
        const notificationService = this.injector.get(NotificationService)
        const translateService = this.injector.get(TranslateService)

        let title = translateService.instant('error.translation.title')
        if (title === 'error.translation.title') title = 'Chyba načítání'

        let msg = translateService.instant('error.translation.message')
        if (msg === 'error.translation.message')
          msg = 'Nepodařilo se načíst jazykové soubory. Zkuste prosím obnovit stránku (F5).'

        notificationService.error(msg, title)
      } catch (e) {
        console.error('Could not show translation error notification', e)
      }
      return
    }

    // Transform DOM Event into a meaningful Error object for Sentry
    const isDomEvent =
      underlyingError &&
      ((typeof Event !== 'undefined' && underlyingError instanceof Event) ||
        (typeof underlyingError === 'object' &&
          typeof (underlyingError as any).type === 'string' &&
          ('target' in underlyingError || 'srcElement' in underlyingError) &&
          !('stack' in underlyingError)))

    let errorToSend = error

    if (underlyingError instanceof Error) {
      errorToSend = underlyingError
    } else if (isDomEvent) {
      const eventType = (underlyingError as any).type || 'error'
      const target = ((underlyingError as any).target || (underlyingError as any).srcElement) as any
      let detail = ''

      if (target) {
        const tagName = target.tagName ? String(target.tagName).toLowerCase() : ''
        const resourceUrl = target.src || target.href || target.currentSrc || ''
        if (tagName && resourceUrl) {
          detail = ` for <${tagName}> resource: ${resourceUrl}`
        } else if (resourceUrl) {
          detail = ` for resource: ${resourceUrl}`
        } else if (tagName) {
          detail = ` on <${tagName}> element`
        }
      }

      const meaningfulError = new Error(`DOM Event [${eventType}] failure${detail}`)
      meaningfulError.name = 'DOMEventError'
      try {
        Sentry.setContext('DOMEvent', {
          type: (underlyingError as any).type,
          targetTagName: target?.tagName,
          targetSrc: target?.src || target?.href || target?.currentSrc,
          detail: (underlyingError as any)?.detail,
        })
      } catch (e) {
        // ignore
      }
      errorToSend = meaningfulError
    } else if (typeof underlyingError === 'string') {
      errorToSend = new Error(underlyingError)
    } else if (
      !underlyingError ||
      (typeof underlyingError === 'object' &&
        Object.keys(underlyingError).length === 0 &&
        !underlyingError.message &&
        !underlyingError.name &&
        !underlyingError.status)
    ) {
      console.warn('Ignored unknown empty error:', error)
      return
    } else if (typeof underlyingError === 'object' && !underlyingError.message && !underlyingError.name) {
      try {
        errorToSend = new Error(`Non-Error exception: ${JSON.stringify(underlyingError)}`)
      } catch {
        errorToSend = new Error('Non-Error object exception')
      }
    }

    this.sentryErrorHandler.handleError(errorToSend)
  }
}
